import React, { useState } from "react";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import Modal, { ModalProps } from "components/Modal";
import clsx from "clsx";
import {
  IconCheck,
  IconExclamationMark,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import { ExclamationIcon } from "@heroicons/react/outline";

interface ConfirmModalProps extends ModalProps {
  title: string | React.ReactNode;
  descriptionMesage: string | React.ReactNode;
  confirmButtonTitle: string | React.ReactNode;
  confirmButtonIcon?: React.ReactElement;
  confirmButtonType?: "warning" | "error";
  secondaryButtonIcon?: React.ReactElement;
  onConfirm: () => Promise<void> | void;
  type?: "warning" | "info" | "error" | "success";
  buttonClassName?: string;
  containerClassName?: string;
  hideCancel?: boolean;
  cancelTitle?: string;
  secondaryButtonTitle?: string;
  secondaryButtonAction?: () => void;
  secondaryButtonLoading?: boolean;
}

export default function ConfirmModal({
  open,
  close,
  title,
  descriptionMesage,
  confirmButtonTitle,
  onConfirm,
  type = "warning",
  buttonClassName,
  containerClassName,
  hideCancel,
  cancelTitle,
  ...props
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    setLoading(true);
    await onConfirm?.();
    setLoading(false);
    close();
  };

  return (
    <Modal
      isOpen={open}
      setIsOpen={close}
      containerClassName={clsx("md:min-w-[500px]", containerClassName)}
      inProgress={loading}
      closeButton={false}
      footer={
        <div className="flex w-full justify-end">
          {!hideCancel ? (
            <div className="mr-6">
              {!loading && (
                <Button width="fit-content" type="gray" onClick={close}>
                  {cancelTitle ?? t("addBuilding.cancel")}
                </Button>
              )}
            </div>
          ) : null}
          {props.secondaryButtonTitle && (
            <div className="mr-6">
              <Button
                width="fit-content"
                type="gray"
                onClick={props.secondaryButtonAction}
                loading={props.secondaryButtonLoading}
                trailingIcon={props.secondaryButtonIcon}
              >
                {props.secondaryButtonTitle}
              </Button>
            </div>
          )}
          <Button
            width={type === "success" ? "fit-container" : "fit-content"}
            type="primary"
            onClick={confirm}
            loading={loading}
            className={clsx(
              buttonClassName,
              props.confirmButtonType === "error"
                ? "!bg-red-600 hover:!bg-red-500"
                : ""
            )}
            trailingIcon={props.confirmButtonIcon}
          >
            {confirmButtonTitle}
          </Button>
        </div>
      }
      footerClassName={type === "success" ? "bg-white pt-0" : "p-6"}
    >
      <div
        className={clsx("p-6 flex w-full", {
          "flex-col items-center": type === "success",
        })}
      >
        {type !== "success" && (
          <div>
            <span
              className={clsx("min-w-fit block p-2 rounded-full", {
                "bg-yellow-100": type === "warning",
              })}
            >
              {type === "warning" && (
                <ExclamationIcon
                  width={24}
                  className="text-yellow-600"
                  strokeWidth={2.5}
                />
              )}
              {type === "info" && (
                <IconInfoCircle
                  width={40}
                  className="text-indigo-600 bg-indigo-100 w-10 h-10 rounded-full p-2.5"
                  stroke={2.5}
                />
              )}
              {type === "error" && (
                <IconExclamationMark
                  width={40}
                  className="text-red-600 bg-red-100 w-10 h-10 rounded-full p-2.5"
                  stroke={2.5}
                />
              )}
            </span>
          </div>
        )}
        {type === "success" && (
          <div className="mb-5 flex items-center justify-center">
            <span
              className={clsx(
                "min-w-[64px] min-h-[64px] p-2 rounded-full flex items-center justify-center",
                {
                  "bg-green-100 p-4": type === "success",
                }
              )}
            >
              {type === "success" && (
                <IconCheck width={24} className="text-green-600" stroke={2} />
              )}
            </span>
          </div>
        )}
        <div
          className={clsx("text-gray-700 text-sm font-normal pl-4 flex-grow", {
            "!pl-0 flex flex-col items-center": type === "success",
          })}
        >
          <div
            className={clsx(
              "text-gray-900 text-lg font-semibold pb-2 flex justify-between w-full",
              {
                "text-center flex justify-center !w-auto": type === "success",
              }
            )}
          >
            <span>{title}</span>
            {type !== "success" && (
              <IconX
                className={
                  "!min-w-7 !min-h-7 px-0 text-gray-500 hover:bg-gray-200 rounded-full p-1 transition cursor-pointer"
                }
                onClick={close}
              />
            )}
          </div>
          <span className="text-gray-700">{descriptionMesage}</span>
        </div>
      </div>
    </Modal>
  );
}
